import { Plus } from 'lucide-react';

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';

import { AvatarWithDetails } from '@/shared/components/avatar';

import { cn } from '@/lib/utils';

import { useRichTextEditor } from '../rich-text-editor-provider';

interface Props {
	command: (item: { id: string; label: string }) => void;
	query: string;
}

const MentionList = forwardRef(({ command, query }: Props, ref) => {
	const { mentionProps } = useRichTextEditor();
	const [selectedIndex, setSelectedIndex] = useState(0);

	const mentionItems = useMemo(() => mentionProps?.items || [], [mentionProps?.items]);

	const filteredItems = useMemo(() => {
		return mentionItems
			.filter(
				(item) =>
					item.name.toLowerCase().includes(query.toLowerCase()) ||
					item.email?.toLowerCase()?.includes(query.toLowerCase())
			)
			.slice(0, 10);
	}, [mentionItems, query]);

	const selectItem = (index: number) => {
		if (index === filteredItems.length) {
			mentionProps?.onAddNewMentionClick?.();
			return;
		}

		const item = filteredItems[index];
		if (item) command({ id: item.email || item.id, label: item.name });
	};

	useEffect(() => setSelectedIndex(0), [filteredItems]);

	useImperativeHandle(ref, () => ({
		onKeyDown: (event: KeyboardEvent) => {
			const totalLength = filteredItems.length + (mentionProps?.showAddNewMentionButton ? 1 : 0);
			if (event.key === 'ArrowUp') {
				setSelectedIndex((selectedIndex + totalLength - 1) % totalLength);
				return true;
			}

			if (event.key === 'ArrowDown') {
				setSelectedIndex((selectedIndex + 1) % totalLength);
				return true;
			}

			if (event.key === 'Enter') {
				selectItem(selectedIndex);
				event.preventDefault();
				event.stopPropagation();
				return true;
			}

			return false;
		},
	}));

	const notFoundContent = useMemo(() => {
		return mentionProps?.contentOnResultNotFound?.(query);
	}, [query, mentionProps?.contentOnResultNotFound]);

	return (
		<div className="flex flex-col gap-0.5 overflow-auto rounded-md border border-slate-200 bg-white p-1.5">
			{filteredItems.length > 0 ? (
				filteredItems.map((item, index) => (
					<AvatarWithDetails
						key={item.id}
						name={item.name}
						email={item.email || ''}
						imageUrl={item.avatarUrl}
						small
						className={cn('rounded p-1', index === selectedIndex ? 'bg-slate-50' : '')}
						onClick={() => command({ id: item.email || item.id, label: item.name })}
						squared={false}
					/>
				))
			) : query?.length > 0 && mentionItems?.length > 0 ? (
				notFoundContent || (
					<p className="max-w-[200px] break-words p-2 text-xs text-secondary">
						No results for {`"${query}"`}.
					</p>
				)
			) : (
				<p className="max-w-[200px] p-2 text-xs text-secondary">
					{mentionProps?.emptyPlaceholderText || 'No mentions present'}
				</p>
			)}

			{mentionProps?.showAddNewMentionButton && (
				<Button
					variant={'ghost'}
					className={cn(
						'h-8 justify-start py-1 text-start text-[12px]',
						mentionItems.length === selectedIndex ? 'bg-slate-50' : ''
					)}
					onClick={mentionProps?.onAddNewMentionClick}
				>
					<Plus className="size-4" />
					{mentionProps?.addOptionText || 'Add new mention'}
				</Button>
			)}

			{mentionProps?.additionalChildren || null}
		</div>
	);
});

MentionList.displayName = 'MentionList';

export default MentionList;
